/*
 * Global CSS for the viewer, injected as a <style> element at the app root.
 *
 * WHY NOT THE THEME. The obvious home for this is `createUnifiedTheme({ components: {
 * MuiCssBaseline: { styleOverrides: {...} } } })`, and an earlier version put it there. It never
 * applied: `UnifiedThemeProvider` (@backstage/theme) renders the v4 and v5 `ThemeProvider`s and
 * **no `<CssBaseline/>`**, and MuiCssBaseline overrides are emitted only by a CssBaseline. So the
 * catalog-table readability rules sat in the theme doing nothing in production for months, which
 * is why the description never actually wrapped. `AppRootElementBlueprint` renders an element at
 * the app root regardless of the theme plumbing — the same extension point the Guide dock uses —
 * so a plain <style> here is guaranteed to apply. No upstream source is modified.
 *
 * SCOPING. `.react-grid-layout`/`.react-grid-item` are react-grid-layout's own library classes,
 * and plugin-home is the only @backstage dependency that pulls it in (verified via package.json),
 * so the home rules touch no other page. The table rules use MUI's stable global classes; the one
 * genuinely app-wide-risky rule an earlier draft carried — `min-width: 360px` on a text cell,
 * which would force horizontal overflow on other tables — is deliberately omitted. The link-cell
 * caps free width that the auto-layout hands to the description instead.
 */
import { AppRootElementBlueprint } from '@backstage/frontend-plugin-api';

const VIEWER_CSS = `
/* Single-scrollbar shell.

   The scaffolded index.html (deploy/raspberry-pi/backstage-app/packages/app/public/index.html) ships
   a bare <div id="root"> with no height chain. Two things follow, and both are fixed here.

   1. Height chain. Percentage heights need an unbroken parent chain, so without html/body/#root at
      100% the app is not bounded to the viewport at all.

   2. Share the height. Even with the chain, the shell renders ~56px taller than the viewport: the
      layout stacks a fixed top bar AND a <main> that is sized to the FULL viewport, so they sum to
      viewport + top-bar and the document scrolls behind <main> (the second scrollbar). The fix is
      NOT to clip — that hides <main>'s bottom — but to make the layout a flex column bounded to the
      viewport, so the top bar and <main> SHARE it: <main> flexes to the space left over and scrolls
      its own content, showing everything. min-height:0 is what lets a flex child shrink below its
      content so its own overflow:auto engages.

   Diagnosed against the live DOM, not guessed: #root was 827 tall, #root > div was 883 (56 top bar +
   827 main), and <main> (overflow:auto) held 2825px of content.

   SCOPED to :has(main), and that is load-bearing. AppRootElementBlueprint renders the version
   badge, the toast container, the Guide dock and this CSS injector as sibling <div>s DIRECTLY under
   #root, alongside the app-shell div — so an unscoped #root > div matches them too, and the
   height:100% stretched the fixed version badge to the full viewport (a gray full-height band).
   Scoping with :has(main) selects only the div that actually holds the page, leaving the overlay
   wrappers their own size.
   The sidebar is position:fixed and out of the shell div's flow, so the flex column touches only the
   top bar and <main>. */
html, body, #root { height: 100%; }
#root > div:has(main) { height: 100%; display: flex; flex-direction: column; min-height: 0; }
#root > div:has(main) > main { flex: 1 1 auto; min-height: 0; }

/* Data tables: content-sized columns, so the description gets the slack a wide screen has while the
   short columns (name, owner, tags, actions) take only what they need.

   The load-bearing rule is width:auto !important on every cell. @material-table/core assigns each
   column WITHOUT an explicit width an equal share — inline style="width: calc((100% - used) / N)" —
   so Name and Owner (which the catalog gives no width) each demand ~half the table, and the
   description column (width 'auto') is crushed to a truncated sliver. table-layout:auto alone does
   nothing about that, because an inline width beats the auto algorithm; forcing width back to auto
   strips material-table's calc() and lets the browser distribute by content — the description
   expands, the short columns shrink. This WAS the bug: the previous "auto layout" fix added no
   override, so material-table's inline widths still ran and the description stayed crushed on a
   surface no unit test could see.

   The trade-off — a long description wanting more width than there is — is handled by clamping cell
   text to a few lines with an ellipsis (full text on hover, via OverflowTooltip's own tooltip),
   never by constraining the column. Words never break mid-way (overflow-wrap: normal).

   Verified by rendering the REAL @material-table/core with the catalog's column shape at desktop and
   mobile widths — not by eye, and not by a hand-built mock, which is what missed material-table's
   inline widths the first time. */
.MuiTable-root { width: 100%; }
.MuiTable-root td.MuiTableCell-root,
.MuiTable-root th.MuiTableCell-root {
  width: auto !important;
  vertical-align: top;
}
.MuiTableCell-root .MuiTypography-root {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: normal;
  /* 1.5, not MUI's tighter body2 (~1.43): with -webkit-line-clamp + overflow:hidden, Chrome shaves
     the descenders (g j p q y) off the LAST visible line when line-height is below ~1.5, because the
     clamp box height is exactly N lines and a too-tight line leaves no room below the baseline. 1.5
     gives that room. Global to every table cell, so it fixes the clamped descriptions and the
     one-line name/owner cells alike, in every table and in the mobile 2-line clamp that inherits it. */
  line-height: 1.5;
}
/* The name is an identifier, not prose: keep it on one line with an ellipsis rather than clamping
   it like a description. Name is always the first RENDERED column in every Kind (the hidden title
   column is not in the DOM), so first-child is a stable hook. */
.MuiTable-root td.MuiTableCell-root:first-child .MuiTypography-root {
  display: block;
  -webkit-line-clamp: unset;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* No column may break a single word mid-way. An owner ("household") rendered as "househol / d", and a
   template type as "declarat / ion", because something on those cells sets word-break: break-all —
   and the description rule above only reset overflow-wrap, which does NOT counter word-break. Reset
   both, on the cell and every descendant (the owner is an entity link, not a Typography, so the rule
   above never reached it). Once the word is unbreakable it becomes the column's min-content width, so
   the content-sized auto-layout gives that column at least the word's width — which is why this fixes
   the owner, type, system and lifecycle columns at whatever position each table happens to put them,
   rather than a single column index. This replaces an earlier nth-child(2) rule that only caught the
   tables where the short column was second. The description still wraps between words and clamps to
   three lines; forbidding mid-word breaks does not touch prose, which wraps at spaces. */
.MuiTable-root td.MuiTableCell-root,
.MuiTable-root td.MuiTableCell-root * {
  word-break: normal !important;
  overflow-wrap: normal !important;
}
/* Center the row-action icons under their centered ACTIONS header. material-table renders them in
   a display:flex wrapper that defaults to flex-start (left), while it centers the column header —
   so the two disagree. The actions cell is the one holding icon buttons; nudge its flex wrapper to
   center. Only the actions column has icon buttons (Name/Owner are links, not buttons), so this
   targets it without a fragile column index. */
.MuiTable-root td.MuiTableCell-root:has(.MuiIconButton-root) div[style*="flex"] {
  justify-content: center;
}

/* Home page: turn the customizable grid into a plain vertical stack, so the welcome card grows
   to its content instead of scrolling inside a fixed cell whose height a saved layout pins. */
.react-grid-layout { height: auto !important; }
.react-grid-layout .react-grid-item {
  position: static !important;
  transform: none !important;
  width: 100% !important;
  height: auto !important;
  margin-bottom: 16px;
}
.react-grid-layout .react-grid-item [class*="MuiCardContent-root"] { overflow: visible !important; }

/* ---------------------------------------------------------------------------
   Mobile (<= 600px, where Backstage collapses the sidebar into a bottom bar).
   The auto-layout base already reads far better here than the old fixed layout,
   which crushed all five columns at once until the headers overlapped. The
   columns keep their content width and the table scrolls sideways for the last
   two — a readable row beats a crushed one. This block only tightens the cells,
   clamps a line shorter than the desktop three, and stops the toolbar's title
   and search box from colliding. */
@media (max-width: 600px) {
  .MuiTable-root td.MuiTableCell-root,
  .MuiTable-root th.MuiTableCell-root {
    padding: 8px 10px;
    font-size: 0.8125rem;
  }
  /* Two lines here rather than the desktop three; the base rule set the box, this just shortens
     it. The first-column name rule above still wins (higher specificity), so names stay one line. */
  .MuiTableCell-root .MuiTypography-root { -webkit-line-clamp: 2; }
  /* The table's toolbar (its title, e.g. "Owned Systems", and the search box) lives in the same
     Paper as the table. On a narrow screen the two overlap; let the search wrap full-width below
     the title. Scoped with :has to Papers that actually contain a table, so the app header and
     other toolbars are untouched. */
  .MuiPaper-root:has(.MuiTable-root) .MuiToolbar-root { flex-wrap: wrap; row-gap: 8px; }
  .MuiPaper-root:has(.MuiTable-root) .MuiToolbar-root .MuiFormControl-root {
    width: 100%;
    margin-left: 0;
  }
  /* Agents page (overlays/backstage/fleet/AgentsPage.tsx). Its cards are built from inline styles,
     which cannot carry a media query, so the responsive behaviour lives here keyed to two classes
     the page sets. The bug this fixes: on a phone the two-column "label | value" grid squeezed the
     value to a sliver and its right-aligned buttons (the scan-schedule "Change") clipped off the
     card edge. !important is required because it overrides an inline grid-template-columns, the one
     thing a plain selector cannot beat. */
  dl.ss-fields { grid-template-columns: 1fr !important; row-gap: 2px; }
  dl.ss-fields > dt { margin-top: 8px; }
  dl.ss-fields > dt:first-of-type { margin-top: 0; }
  /* The per-action tables (backups, off-box copies) are three columns with a no-wrap button cell,
     which forces horizontal scroll on a narrow card. Lay each row's cells out as stacked blocks so
     nothing is pushed off-screen; the buttons then sit left-aligned under their row rather than
     clipped at the right. */
  table.ss-fleet-table,
  table.ss-fleet-table tbody,
  table.ss-fleet-table tr,
  table.ss-fleet-table td { display: block; width: auto; }
  table.ss-fleet-table tr { padding: 6px 0; }
  table.ss-fleet-table td {
    padding: 2px 0 !important;
    text-align: left !important;
    white-space: normal !important;
  }
}
`;

const ViewerGlobalCss = () => <style dangerouslySetInnerHTML={{ __html: VIEWER_CSS }} />;

export const viewerGlobalCss = AppRootElementBlueprint.make({
  name: 'stackschematic-viewer-css',
  params: { element: <ViewerGlobalCss /> },
});
